/**
 * Who you are, and the way out.
 *
 * The export draws three cards — account, password, delete — and this screen has two of them. The
 * password card is not built because no endpoint changes a password; see AGENTS.md.
 *
 * Two things this screen says differently from the design, each because the API says so:
 *
 *   - **No display name.** An account is an email address and a password, Argon2id, no OAuth
 *     anywhere. `workspace_name` is not a stand-in — the service derives it from the local part of
 *     the email, so showing both would print the same string twice. (The current export has also
 *     dropped the design's "· from your Google account", which was never reproduced here.)
 *   - **The delete confirmation asks for the account's own email address**, not the phrase
 *     "delete everything". That is what `AccountDeleteRequest` actually checks, and asking for
 *     something the API does not verify is theatre: a UI-only confirmation protects nobody who is
 *     not using the UI.
 *
 * Gone with the export: the Usage card, the Export card and the Sign out button. The rail has a
 * sign-out on every screen. `GET /api/v1/usage` and `GET /api/v1/export` now have no caller
 * anywhere in this app — recorded in AGENTS.md, because the second of those was the only way a
 * person could get their knowledge out.
 */

import { useState } from "react";

import { Dialog } from "../components/dialog";
import * as authApi from "../lib/api/auth-api";
import { ApiError } from "../lib/api/errors";
import { useAuth } from "../lib/auth/auth-context";
import { getFreshAccessToken } from "../lib/auth/session";
import { absoluteDate } from "../lib/ui/format";

export function SettingsScreen() {
  const auth = useAuth();
  const account = auth.account;

  const [deleting, setDeleting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  async function doDelete() {
    if (account === null) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const accessToken = await getFreshAccessToken();
      await authApi.deleteAccount(accessToken, confirmEmail.trim());
      setConfirmingDelete(false);
      // Sign out locally so the guard sends them to the sign-in screen. Their credentials are
      // already dead server-side — the API revokes every token and ends every session first.
      await auth.signOut();
    } catch (cause) {
      setDeleteError(
        cause instanceof ApiError ? cause.message : "Could not delete the account. Try again.",
      );
      setDeleting(false);
    }
  }

  const emailMatches =
    account !== null && confirmEmail.trim().toLowerCase() === account.email.toLowerCase();

  return (
    <>
      <h1 className="page-title">Account &amp; workspace</h1>

      <div className="card">
        <p className="card__label">Account</p>
        <p className="card__title">{account?.email ?? "—"}</p>
        <p className="card__body">
          Workspace <span className="mono">{account?.workspace_name ?? "—"}</span>
          {account?.created_at !== undefined && <> · joined {absoluteDate(account.created_at)}</>}
        </p>
        <p className="card__body card__body--quiet">
          Athena knows you by your email address. There is no display name and no connected Google
          account — registration is an email and a password.
        </p>
        {/*
          The design's Sign out button used to sit here and the current export has removed it. The
          sidebar has one on every screen, so nothing is lost by following that.
        */}
      </div>

      <div className="card card--danger">
        <p className="card__label">Danger</p>
        <p className="card__title">Delete account</p>
        {/*
          Verbatim from the export. The previous wording ended "Export first if you want your
          knowledge", which would now point at a card that is gone — and the export's own sentence
          does not say it, so this is a transcription rather than a rewrite.
        */}
        <p className="card__body">
          Deletes your workspace, everything in it and every token, immediately and permanently.
          Assistants lose access the same second.
        </p>
        <button
          className="button-secondary"
          type="button"
          onClick={() => {
            setConfirmEmail("");
            setAcknowledged(false);
            setDeleteError(null);
            setConfirmingDelete(true);
          }}
        >
          Delete account…
        </button>
      </div>

      {confirmingDelete && account !== null && (
        <Dialog
          title="Delete your account?"
          onClose={() => {
            setConfirmingDelete(false);
          }}
          footer={
            <>
              <button
                className="button-quiet"
                type="button"
                onClick={() => {
                  setConfirmingDelete(false);
                }}
              >
                Cancel
              </button>
              <button
                className="button-secondary"
                type="button"
                disabled={!emailMatches || !acknowledged || deleting}
                onClick={() => void doDelete()}
              >
                {deleting ? "Deleting…" : "Delete everything"}
              </button>
            </>
          }
        >
          <p className="dialog__body">
            This deletes your workspace, all of your knowledge and every token, immediately. The
            rows themselves are purged shortly afterwards. There is no undo from your side.
          </p>
          <label className="field-label" htmlFor="confirm-email">
            Type <span className="mono">{account.email}</span> to confirm
          </label>
          <input
            id="confirm-email"
            className="field-input"
            value={confirmEmail}
            onChange={(event) => {
              setConfirmEmail(event.target.value);
            }}
            autoComplete="off"
            aria-label="Confirm your email address"
          />
          <label className="checkbox-row">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(event) => {
                setAcknowledged(event.target.checked);
              }}
            />
            <span>I understand this cannot be undone.</span>
          </label>
          {deleteError !== null && (
            <p className="dialog__error" role="alert">
              {deleteError}
            </p>
          )}
        </Dialog>
      )}
    </>
  );
}
